import React, { useState } from "react";
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from "react-router-dom";
import { useFormContext } from "../../context/Context";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { RiLockPasswordFill, RiLockPasswordLine } from "react-icons/ri";
import { BiSolidHide, BiSolidShow } from "react-icons/bi";
import axiosPublic from "../../services/axios/axiosPublic";
import api from "../../services/api";
import { BsCheckLg, BsXLg } from "react-icons/bs";
import PasswordCriteria from "utils/PasswordCriteria";
import { useForm } from "react-hook-form";

const PASSWORD_CRITERIA = PasswordCriteria();

const ValidationLine = ({ condition, text }) => (
  <div
    className={condition ? "text-success" : "text-danger"}
    style={{ fontSize: "0.8rem", lineHeight: "1.4" }}
  >
    {condition ? (
      <BsCheckLg style={{ marginRight: "5px" }} />
    ) : (
      <BsXLg style={{ marginRight: "5px" }} />
    )}
    {text}
  </div>
);

ValidationLine.propTypes = {
  condition: PropTypes.bool.isRequired,
  text: PropTypes.string.isRequired,
};

export default function Resetpassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    clearErrors,
  } = useForm();
  const { serverError, setServerError } = useFormContext();

  const [resetPasswordToken] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("token");
  });
  const [resetPasswordShow, setResetPasswordShow] = useState(false);
  const [resetConfirmPasswordShow, setResetConfirmPasswordShow] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const watchedPassword = watch("resetPassword", "");
  const watchedConfirmPassword = watch("resetConfirmPassword", "");

  // Calculate password criteria 
  const criteriaStatus = {};
  PASSWORD_CRITERIA.forEach((item) => {
    criteriaStatus[item.key] = item.regex.test(watchedPassword);
  });

  const showPasswordIcons = watchedPassword && watchedPassword.length > 0;
  const showConfirmIcons = watchedConfirmPassword && watchedConfirmPassword.length > 0;

  const shouldShowCriteria = watchedPassword.length > 0 || isPasswordFocused;
  const isPatternError = errors.resetPassword?.type === 'pattern';
  const isRequiredError = errors.resetPassword?.type === 'required';
  const shouldShowPatternErrorMessage = isPatternError && !shouldShowCriteria;

  const handleResetPasswordVisibility = () => {
    setResetPasswordShow(!resetPasswordShow);
  };

  const handleResetConfirmPasswordVisibility = () => {
    setResetConfirmPasswordShow(!resetConfirmPasswordShow);
  };

  const clearErrorFields = (field) => {
    clearErrors(field);
    setServerError("");
  };

  const handleFormSubmit = async (data) => {
    const resetPassword = data.resetPassword;
    try {
      const formData = new URLSearchParams();
      formData.append("token", resetPasswordToken);
      formData.append("newPassword", resetPassword);

      const response = await axiosPublic.post(api.resetPassword, formData);
      if (response?.data?.success === true) {
        toast.success(response?.data?.reason, {
          className: "toast-success-custom",
        });
        reset();
        setServerError("");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } else {
        toast.error(response?.data?.reason, {
          className: "toast-error-custom",
          autoClose: 8000,
        });
      }
    } catch (error) {
      toast.error(error?.data?.reason || "Unable to connect to server", {
        className: "toast-error-custom",
        autoClose: 8000,
      });
    }
  };

  return (
    <div className="container-fluid container-fluid-login">
      <div className="main-logo">
        <div className="logo-image">
          <img src={require("assets/img/company_logo.png")} alt="..." />
        </div>
      </div>
      <div className="login-container">
        <div className="login-row">
          <div className="col-lg-12 col-md-10 col-sm-8 col-xs-12 left login-row-inner">
            <div className="login-form">
              <div className="title">
                <p className="welcome">Reset Password</p>
              </div>
              <div className="form" style={{ marginTop: "-42px" }}>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                  <p
                    className="login-title"
                    style={{ color: "#5A5A5A", textAlign: "center" }}
                  >
                    Create a new password for login
                  </p>
                  <div className="form-detail">
                    <div
                      className={`input-text ${errors.resetPassword ? "error" : ""
                        }`}
                    >
                      <p className="logo-txt" style={{ color: "#5A5A5A" }}>
                        New Password
                      </p>
                      <div className="input-icons">
                        <RiLockPasswordLine className="icon" />
                        <input
                          className="login-input-field"
                          type={resetPasswordShow ? "text" : "password"}
                          placeholder="Enter a new password"
                          {...register("resetPassword", {
                            required: "New Password is required",

                            onChange: () => clearErrorFields("resetPassword"),
                            onFocus: () => setIsPasswordFocused(true),
                            onBlur: () => {
                              if (watchedPassword.length === 0) {
                                setIsPasswordFocused(false);
                              }
                            }
                          })}
                        />
                        {showPasswordIcons && (
                          <button
                            className="visibility-password"
                            type="button"
                            onClick={() => handleResetPasswordVisibility()}
                          >
                            {resetPasswordShow ? (
                              <BiSolidHide />
                            ) : (
                              <BiSolidShow />
                            )}
                          </button>
                        )}

                        {shouldShowCriteria && (
                          <div
                            className="password-criteria visible p-2 mt-2 border rounded"
                            style={{ backgroundColor: "#f8f9fa" }}
                          >
                            <strong className="d-block mb-1">
                              Password Requirements:
                            </strong>
                            {PASSWORD_CRITERIA.map((item) => (
                              <ValidationLine
                                key={item.key}
                                condition={criteriaStatus[item.key]}
                                text={item.message}
                              />
                            ))}
                          </div>
                        )}

                        {isRequiredError && (
                          <p className="reset-error-msg">
                            {errors.resetPassword.message}
                          </p>
                        )}

                        {shouldShowPatternErrorMessage && (
                          <p className="reset-error-msg">
                            {errors.resetPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      className={`input-text ${errors.resetConfirmPassword ? "error" : ""
                        } confirm ${shouldShowCriteria ? "with-reset-criteria" : ""
                        }`}
                    >
                      <p className="logo-txt confirm">Confirm Password</p>
                      <div className="input-icons">
                        <RiLockPasswordFill className="icon" />
                        <input
                          className="login-input-field"
                          type={resetConfirmPasswordShow ? "text" : "password"}
                          placeholder="Confirm your Password"
                          {...register("resetConfirmPassword", {
                            required: "Confirm Password is required",
                            validate: (value) =>
                              value === watchedPassword ||
                              "Password does not match",
                            onChange: () => clearErrorFields("resetConfirmPassword"),
                          })}
                        />
                        {showConfirmIcons && (
                          <button
                            className="reset-confirm-visibility-password"
                            type="button"
                            onClick={handleResetConfirmPasswordVisibility}
                          >
                            {resetConfirmPasswordShow ? (
                              <BiSolidHide />
                            ) : (
                              <BiSolidShow />
                            )}
                          </button>
                        )}
                        {errors.resetConfirmPassword && (
                          <p className="reset-error-msg">
                            {errors.resetConfirmPassword.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <div className="reset-btn">
                        <button className="login" type="submit">
                          Reset password
                        </button>
                      </div>
                    </div>
                    <ToastContainer />
                  </div>
                </form>
                {serverError && (
                  <div className="reset-error-message">{serverError}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}